import { createSlice } from '@reduxjs/toolkit'
import { v4 as uuidv4 } from 'uuid'
import { ICartItem } from '../types'
import { IDiscount, IProduct } from '../../_types'

const initialState = {
  cart: [] as ICartItem[],
}

export const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    ADD_TO_CART: (state, action: { payload: ICartItem & { key?: string } }) => {
      if (
        action.payload.type == 'simple' &&
        action.payload.order_modifiers.length == 0
      ) {
        const itemInCart = state.cart.find(
          (item) => item.product_id === action.payload.product_id
        )
        if (itemInCart) {
          if (!itemInCart?.default_product) itemInCart.quantity++
        } else {
          state.cart.push({
            ...action.payload,
            quantity: action.payload.quantity ? action.payload.quantity : 1,
            key: uuidv4(),
          })
        }
      } else {
        state.cart.push({
          ...action.payload,
          quantity: action.payload.quantity ? action.payload.quantity : 1,
          key: uuidv4(),
        })
      }
    },
    UPDATE_CART: (state, action) => {
      state.cart = action.payload
    },
    SET_QUANTITY: (state, action) => ({
      ...state,
      cart: state.cart.map((item) =>
        item.key === action.payload?.key
          ? { ...item, quantity: action.payload?.quantity }
          : item
      ),
    }),
    INCREMENT: (state, action) => ({
      ...state,
      cart: state.cart.map((item) =>
        item.key === action.payload
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ),
    }),
    DECREMENT: (state, action) => ({
      ...state,
      cart: state.cart.map((item) =>
        item.key === action.payload
          ? { ...item, quantity: item.quantity === 1 ? 1 : item.quantity - 1 }
          : item
      ),
    }),
    REMOVE: (state, action) => ({
      ...state,
      cart: state.cart.filter((item) => item.key !== action.payload),
    }),
    UPDATE_MODIFIERS: (state, action) => ({
      ...state,
      cart: state.cart.map((item) =>
        item.key === action.payload.key
          ? { ...item, order_modifiers: action.payload.data }
          : item
      ),
    }),
    STOP: (state, action) => {
      state.cart = state.cart.map((item) =>
        item.product_id === action.payload ? { ...item, in_stop: true } : item
      )
    },
    EXCLUDE: (state, action) => {
      state.cart = state.cart.map((item) =>
        item.product_id === action.payload && item.in_stop
          ? { ...item, in_stop: false }
          : item
      )
    },
    EXCLUDE_STOP: (state) => {
      state.cart = state.cart.map((item) =>
        item.in_stop ? { ...item, in_stop: false } : item
      )
    },
    APPLY_EMBEDDED_DISCOUNTS: (
      state,
      action: { payload: Record<string, IProduct> }
    ) => {
      state.cart = state.cart.map((item) => {
        const product = action.payload[item.product_id]
        if (!product?.discounts?.length) {
          return item
        }

        const totalDiscount = product.discounts.reduce(
          (acc, cur) => acc + cur.discount_price,
          0
        )

        return {
          ...item,
          price_with_discount: item.price + totalDiscount,
          discount_percent:
            totalDiscount !== 0
              ? Math.abs(Math.round((totalDiscount / item.price) * 100))
              : 0,
        }
      })
    },
    UPDATE_PRICES: (state, action: { payload: IDiscount[] }) => {
      state.cart = state.cart.map((item) => {
        const totalDiscount = action?.payload?.reduce((acc, cur) => {
          if (
            cur.discount_id === 'e46ce26c-5f1b-4ca6-89f1-c013ed5f58ce' &&
            item.product_id !== '673c4d0a-1368-40ff-bef5-32c57dbbf964'
          ) {
            return acc
          }
          if (
            cur?.product_and_discount &&
            Object.keys(cur?.product_and_discount).length > 0
          ) {
            if (cur.product_and_discount[item.product_id]) {
              acc += cur.product_and_discount[item.product_id]
            }
          } else if (cur.discount_for === 'product') {
            if (cur.discount_mode === 'fixed') {
              acc -= cur.discount_amount
            } else if (cur.discount_mode === 'percent') {
              acc -= (item.price / 100) * cur.discount_amount
            }
          }
          return acc
        }, 0)
        return {
          ...item,
          price_with_discount: item?.price + totalDiscount,
          discount_percent:
            totalDiscount !== 0
              ? Math.abs(Math.round((totalDiscount / item.price) * 100))
              : 0,
        }
      })
    },
    CLEAR: (state) => ({
      ...state,
      cart: [],
    }),
    ADD_COMMENT: (state, action) => {
      const item = state.cart.find((item) => item.key === action.payload.key)
      if (item) {
        item.comment = action.payload.comment
      }
    },

    // Delete comment based on product_id
    DELETE_COMMENT: (state, action) => {
      const item = state.cart.find((item) => item.key === action.payload)
      if (item) {
        item.comment = ''
      }
    },
  },
})

export default cartSlice.reducer
export const {
  ADD_TO_CART,
  INCREMENT,
  UPDATE_CART,
  DECREMENT,
  REMOVE,
  SET_QUANTITY,
  UPDATE_MODIFIERS,
  CLEAR,
  STOP,
  EXCLUDE,
  EXCLUDE_STOP,
  UPDATE_PRICES,
  APPLY_EMBEDDED_DISCOUNTS,
  DELETE_COMMENT,
  ADD_COMMENT,
} = cartSlice.actions
